import { navigateHref } from "../../../features/navigation/navigation.ts";
import { t, useStoreLanguage } from "../../../features/store-language/i18n.ts";
import {
  Children,
  cloneElement,
  isValidElement,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactElement, ReactNode } from "react";
import { Button, Dropdown, Tooltip } from "antd";
import type { MenuProps } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import "./gridActions.css";
import { hasAccessRight } from "../../../utils/accessRights.ts";

interface GridOperationMenuProps {
  /** Legacy renderers are normalized into the same Ant Design dropdown. */
  children?: ReactNode;
  /** New grids should prefer labelled Ant Design menu items. */
  items?: MenuProps["items"];
  label?: string;
}

interface LegacyAction {
  action: ReactNode;
  key: string;
  title: ReactNode;
  wrappers: ReactElement[];
}

export interface GridOperationContextAction {
  danger?: boolean;
  disabled?: boolean;
  href?: string;
  icon?: ReactNode;
  key: string;
  label: string;
  tooltip?: ReactNode;
  onClick?: (...args: any[]) => void;
  target?: string;
  to?: string;
}

function getLegacyText(node: ReactNode): string {
  let text = "";
  Children.forEach(node, (child) => {
    if (typeof child === "string" || typeof child === "number") {
      text += String(child);
      return;
    }
    if (isValidElement(child)) {
      text += getLegacyText((child as ReactElement<any>).props.children);
    }
  });
  return text.trim();
}

function getLegacyActionLabel(element: ReactElement<any>): ReactNode {
  const explicitTitle =
    element.props.title ??
    element.props["data-original-title"] ??
    element.props["aria-label"];
  if (explicitTitle) {
    return explicitTitle;
  }

  const visibleText = getLegacyText(element.props.children);
  return visibleText || null;
}

function collectLegacyActions(
  node: ReactNode,
  actions: LegacyAction[],
  wrappers: ReactElement[] = [],
) {
  Children.forEach(node, (child) => {
    if (!isValidElement(child)) {
      return;
    }

    const element = child as ReactElement<any>;
    if (element.type === Tooltip && element.props.title) {
      actions.push({
        action: element.props.children,
        key: `legacy-action-${actions.length}`,
        title: element.props.title,
        wrappers,
      });
      return;
    }

    const isClickable =
      element.props.onClick != null ||
      element.props.to != null ||
      element.props.href != null;
    if (isClickable) {
      actions.push({
        action: element,
        key: `legacy-action-${actions.length}`,
        title: getLegacyActionLabel(element),
        wrappers,
      });
      return;
    }

    const nextWrappers =
      typeof element.type === "function"
        ? [...wrappers, element]
        : wrappers;
    collectLegacyActions(element.props.children, actions, nextWrappers);
  });
}

function isLegacyActionAllowed(wrappers: ReactElement[]) {
  return wrappers.every((wrapper) => {
    const wrapperType = wrapper.type as {
      gridPermissionGuard?: boolean;
    };
    if (!wrapperType.gridPermissionGuard) {
      return true;
    }

    return hasAccessRight(wrapper.props.id);
  });
}

function getLegacyActionIcon(actionElement: ReactElement<any>): ReactNode {
  const icon =
    typeof actionElement.type === "string" && actionElement.type !== "i"
      ? actionElement.props.children
      : actionElement;

  if (!isValidElement(icon)) {
    return icon;
  }

  const iconElement = icon as ReactElement<any>;
  return cloneElement(iconElement, {
    className: String(iconElement.props.className ?? "")
      .split(" ")
      .filter((className) => {
        return (
          className &&
          !/^fs-\d+$/.test(className) &&
          !/^m[trblxy]?x?-\d+$/.test(className) &&
          className !== "cursor-pointer"
        );
      })
      .join(" "),
    onClick: undefined,
    style: {
      ...iconElement.props.style,
      color: undefined,
      fontSize: undefined,
    },
  });
}

/** Extract commands without mounting a renderer or bypassing its permission wrappers.
 * Mixed dropdowns and standalone links/buttons keep their original order and handlers.
 */
export function getGridOperationContextActions(content: ReactNode): GridOperationContextAction[] {
  const actions: GridOperationContextAction[] = [];
  function visit(node: ReactNode, wrappers: ReactElement[] = [], title?: ReactNode, disabled = false) {
    Children.forEach(node, (child) => {
      if (!isValidElement(child)) return;
      const element = child as ReactElement<any>;
      const props = element.props;
      const nextWrappers = typeof element.type === "function" ? [...wrappers, element] : wrappers;
      if (!isLegacyActionAllowed(nextWrappers)) return;
      const unavailable = disabled || Boolean(props.disabled || props.pending || props.loading);
      const structured = element.type === GridOperationMenu || (element.type as any).gridOperations;
      const menu = element.type === Dropdown ? props.menu : structured ? { items: props.items } : undefined;
      if (menu?.items) {
        function collect(items: MenuProps["items"], inheritedDisabled: boolean) {
          items?.forEach((item) => {
            if (!item || ("type" in item && item.type === "divider")) return;
            const entry = item as any;
            const blocked = inheritedDisabled || Boolean(entry.disabled);
            if (entry.children) { collect(entry.children, blocked); return; }
            const label = getLegacyText(entry.label);
            if (!label) return;
            const link = isValidElement(entry.label) ? (entry.label as ReactElement<any>).props : {};
            actions.push({
              key: String(actions.length) + ":" + entry.key,
              label, icon: entry.icon, disabled: blocked, danger: entry.danger,
              tooltip: isValidElement(entry.label) && entry.label.type === Tooltip ? link.title : undefined,
              to: link.to, href: link.href, target: link.target,
              onClick: (event: any) => {
                const info = { key: String(entry.key), keyPath: [String(entry.key)], domEvent: event };
                const result = entry.onClick?.(info);
                const menuResult = menu.onClick?.(info);
                const linkResult = link.onClick?.(event);
                return Promise.all([result, menuResult, linkResult]);
              },
            });
          });
        }
        collect(menu.items, unavailable);
        return;
      }
      if (props.onClick || props.to || props.href) {
        const label = getLegacyText(title || getLegacyActionLabel(element));
        if (label) actions.push({
          key: "action-" + actions.length, label,
          icon: props.icon || getLegacyActionIcon(element),
          danger: props.danger || /trash|delete|remove/.test(String(props.className || "")),
          disabled: unavailable, to: props.to, href: props.href, target: props.target,
          onClick: props.onClick,
        });
        return;
      }
      visit(props.children, nextWrappers, element.type === Tooltip ? props.title : title, unavailable);
    });
  }
  visit(content);
  return actions;
}

function GridOperationMenu({
  children,
  items,
  label = t("عملیات"),
}: GridOperationMenuProps) {
  useStoreLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<any>(null);
  const legacyActions = useMemo(() => {
    const actions: LegacyAction[] = [];
    collectLegacyActions(children, actions);
    return actions;
  }, [children]);
  const normalizedItems = useMemo<MenuProps["items"]>(() => {
    if (items?.length) {
      return items;
    }

    return legacyActions
      .filter((item) => {
        return isLegacyActionAllowed(item.wrappers);
      })
      .map((item) => {
        const actionElement = item.action as ReactElement<any>;
        const label = getLegacyText(item.title);
        return {
          key: item.key,
          label,
          icon: getLegacyActionIcon(actionElement),
          disabled: actionElement.props.disabled,
          danger: label.includes("حذف"),
          onClick: (info) => {
            actionElement.props.onClick?.(info.domEvent);
            if (typeof actionElement.props.to === "string") {
              navigate(actionElement.props.to);
            } else if (actionElement.props.href) {
              if (actionElement.props.target === "_blank") {
                window.open(
                  actionElement.props.href,
                  "_blank",
                  "noopener,noreferrer",
                );
              } else {
                navigateHref(navigate, actionElement.props.href);
              }
            }
          },
        };
      })
      .filter((item) => {
        return Boolean(item.label);
      });
  }, [items, legacyActions, navigate]);
  const triggerElement =
    triggerRef.current?.nativeElement ?? triggerRef.current;
  const drawerElement = triggerElement?.closest?.(".ant-drawer");
  const drawerZIndex = drawerElement
    ? Number.parseInt(window.getComputedStyle(drawerElement).zIndex, 10)
    : Number.NaN;
  const overlayZIndex = document.body.classList.contains(
    "ag-grid-fullscreen-active",
  )
    ? 10001
    : Number.isFinite(drawerZIndex)
      ? drawerZIndex + 10
      : 900;
  const button = (
    <Tooltip title={label}>
      <Button
        ref={triggerRef}
        size="small"
        className="erp-grid-operation-trigger"
        icon={<DownOutlined />}
        aria-label={label}
      >
        {label}
      </Button>
    </Tooltip>
  );

  if (!normalizedItems?.length) {
    return null;
  }

  return (
    <Dropdown
      menu={{ items: normalizedItems }}
      trigger={["click"]}
      open={open}
      onOpenChange={setOpen}
      overlayClassName="erp-grid-operation-dropdown"
      overlayStyle={{ zIndex: overlayZIndex }}
    >
      {button}
    </Dropdown>
  );
}

export default GridOperationMenu;
